//Built-in libraries

//Third party libraries
import { useLocation, useNavigate } from 'react-router';
import { useApplicationsListContext } from '@/features/ApplicationForm/Components/ApplicationsList/ApplicationsListProvider';

//Services
import { convertEnumToText } from '@/utils';
//Custom components
// import Table from "components/UI/Table";
import { FiltersForm } from './FiltersForm';

//STP Libraries

import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
//Assets and Miscellaneous
// import Styles from "./styles.module.css";
import { useState } from 'react';
import { patchArchiveApplication } from '@/api/Rover/ApplicationForm';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import Confirm from '@/components/UI/Modal/Confirm';
import ReactTable from '@/components/UI/ReactTable';
import { DATE_FORMAT } from '@/constants';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';

//Component Begin

export const ApplicationList = () => {
  const { data, tablePagination, showFilterModal, toggleFilterModal, applyFiltersHandler } = useApplicationsListContext();

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedRow, setSelectedRow] = useState<any[]>([]);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const queryClient = useQueryClient();

  const columnHelper = createColumnHelper<any>();
  const columns = [
    columnHelper.accessor('applicationData', {
      id: 'clientName',
      header: () => 'Name',
      size: 350,
      cell: (info) => {
        const clientInformation = info.getValue()?.application;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{clientInformation?.name}</span>
            <div className='flex flex-col gap-1 text-xs text-neutralColor-normal'>
              <span>{clientInformation?.email}</span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('applicationData', {
      id: 'address',
      header: () => 'Address',
      size: 300,
      cell: (info) => {
        const application = info.getValue()?.application;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{application?.streetAddress}</span>
            <div className='flex flex-col gap-1 text-xs text-neutralColor-normal'>
              <span>
                {application?.city}, {application?.state}, {application?.zipCode}
              </span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('applicationData', {
      id: 'desiredBedrooms',
      header: () => 'Desired Bedrooms',
      size: 200,
      cell: (info) => {
        const application = info.getValue()?.application;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{application?.desiredBedrooms}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => 'Status',
      size: 200,
      cell: (info) => {
        return <Badge status={info.getValue()} text={convertEnumToText(info.getValue())} />;
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => 'Submitted',
      size: 250,
      cell: (info) => {
        return <div className='flex items-center w-full capitalize'>{dayjs(info.getValue()).format(DATE_FORMAT)}</div>;
      },
    }),
  ];

  const archiveApplication = useMutation(
    () => {
      return Promise.all(
        selectedRow.map((data) =>
          patchArchiveApplication({
            applicationId: data.original.id,
          })
        )
      );
    },
    {
      onSuccess: () => {
        setShowArchiveConfirm(false);
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS]);
      },
    }
  );

  const confirmArchiveApplication = async () => {
    archiveApplication.mutate();
  };

  const cancelArchiveApplication = () => {
    setShowArchiveConfirm(false);
  };

  return (
    <>
      {showArchiveConfirm && (
        <Confirm
          title='Archive Application'
          message='You are about to archive selected application(s). Are you sure you want to archive ?'
          acceptButtonLabel='Archive'
          declineButtonLabel='Cancel'
          accept={confirmArchiveApplication}
          decline={cancelArchiveApplication}
        />
      )}

      {selectedRow.length > 0 && (
        <div className='absolute top-4 left-28'>
          <Button
            label='Archive'
            btnStyle='neutral'
            btnSize='md'
            icon={<ArchiveBoxIcon className='h-5 w-5' />}
            onClick={() => setShowArchiveConfirm(true)}
          />
        </div>
      )}

      <ReactTable
        cols={columns}
        data={data?.data}
        onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
        pagination={tablePagination}
        onRowSelect={(val: any[]) => setSelectedRow(val)}
      />

      {showFilterModal && <FiltersForm handleModal={toggleFilterModal} handleApplyFilter={applyFiltersHandler} />}
    </>
  );
};
